import { useState, type ReactNode } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, Skeleton } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { day, money } from '../lib/format.ts';
import { useAction } from './manage/common.tsx';
import { downloadCsv, plain, Table } from './Reports.tsx';

/**
 * The cash flow statement and the partner ledger of Accounting → Statements
 * (modules/accounting cashflow.ts).
 */

type Group = 'customers' | 'suppliers' | 'staff' | 'taxes' | 'other';
interface FlowAccount {
  id: string;
  code: string;
  name: string;
  amount: number;
}
interface CashFlowData {
  from: string;
  to: string;
  opening: number;
  closing: number;
  net: number;
  balanced: boolean;
  sections: {
    operating: { rows: { group: Group; amount: number }[]; total: number };
    investing: { rows: FlowAccount[]; total: number };
    financing: { rows: FlowAccount[]; total: number };
  };
  cash_accounts: { id: string; code: string; name: string; opening: number; closing: number }[];
}

const signed = (n: number) => (n === 0 ? '—' : n > 0 ? `+${money(n)}` : `−${money(-n)}`);

function FlowRow({ label, value, sub, onClick }: { label: string; value: number; sub?: string; onClick?: () => void }) {
  const inner = (
    <>
      <span>
        {label}
        {sub ? <span className="muted small"> {sub}</span> : null}
      </span>
      <span className={`tabular${value < 0 ? ' negative' : ''}`}>{signed(value)}</span>
    </>
  );
  return <li>{onClick ? <button onClick={onClick}>{inner}</button> : <span className="statement-extra cf-row">{inner}</span>}</li>;
}

export function CashFlowStatement({ from, to, version, onAccount }: { from: string; to: string; version: number; onAccount: (id: string) => void }) {
  const { data } = useAction<CashFlowData>('accounting.cash_flow', { from, to, v: version });
  if (!data || data.from !== from || data.to !== to) return <Skeleton h={320} r="var(--radius-lg)" />;
  const s = data.sections;
  const csv = () =>
    downloadCsv(
      `cash-flow-${from}-${to}.csv`,
      [t('acc.c_name'), t('acc.c_amount')],
      [
        [t('cf.opening'), plain(data.opening)],
        ...s.operating.rows.map((r) => [t(`cf.g_${r.group}` as MessageKey), plain(r.amount)]),
        [t('cf.operating'), plain(s.operating.total)],
        ...s.investing.rows.map((r) => [`${r.code} ${r.name}`, plain(r.amount)]),
        [t('cf.investing'), plain(s.investing.total)],
        ...s.financing.rows.map((r) => [`${r.code} ${r.name}`, plain(r.amount)]),
        [t('cf.financing'), plain(s.financing.total)],
        [t('cf.net'), plain(data.net)],
        [t('cf.closing'), plain(data.closing)],
      ],
    );
  const section = (key: 'operating' | 'investing' | 'financing', rows: ReactNode, total: number) => (
    <div className="statement-group">
      <h3>{t(`cf.${key}`)}</h3>
      <ul>{rows}</ul>
      <div className="statement-total">
        <span>{t(`cf.${key}_net`)}</span>
        <span className={`tabular${total < 0 ? ' negative' : ''}`}>{signed(total)}</span>
      </div>
    </div>
  );
  return (
    <section className="panel rep-wide statement" aria-labelledby="cf-title">
      <div className="panel-head">
        <div>
          <h2 id="cf-title">{t('cf.title')}</h2>
          <p className="muted">{t('cf.hint')}</p>
        </div>
        <Button variant="ghost" icon="download" onClick={csv}>
          {t('rep.download')}
        </Button>
      </div>
      <dl className="cf-ends">
        <div>
          <dt>{t('cf.opening_at', { date: day(from) })}</dt>
          <dd className="tabular">{money(data.opening)}</dd>
        </div>
        <div className={data.net < 0 ? 'down' : 'up'}>
          <dt>{t('cf.net')}</dt>
          <dd className="tabular">{signed(data.net)}</dd>
        </div>
        <div>
          <dt>{t('cf.closing_at', { date: day(to) })}</dt>
          <dd className="tabular">{money(data.closing)}</dd>
        </div>
      </dl>
      {section(
        'operating',
        s.operating.rows.map((r) => (r.amount || r.group !== 'other' ? <FlowRow key={r.group} label={t(`cf.g_${r.group}` as MessageKey)} value={r.amount} /> : null)),
        s.operating.total,
      )}
      {section(
        'investing',
        s.investing.rows.length ? s.investing.rows.map((r) => <FlowRow key={r.id} label={r.name} sub={r.code} value={r.amount} onClick={() => onAccount(r.id)} />) : <li className="muted">—</li>,
        s.investing.total,
      )}
      {section(
        'financing',
        s.financing.rows.length ? s.financing.rows.map((r) => <FlowRow key={r.id} label={r.name} sub={r.code} value={r.amount} onClick={() => onAccount(r.id)} />) : <li className="muted">—</li>,
        s.financing.total,
      )}
      {data.cash_accounts.length ? (
        <div className="statement-group">
          <h3>{t('cf.where')}</h3>
          <ul>
            {data.cash_accounts.map((a) => (
              <li key={a.id}>
                <button onClick={() => onAccount(a.id)}>
                  <span>
                    <span className="acc-code">{a.code}</span> {a.name}
                  </span>
                  <span className="tabular">{money(a.closing)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className={`acc-check ${data.balanced ? 'ok' : 'off'}`}>
        <Icon name={data.balanced ? 'checkCircle' : 'alert'} size={16} />
        {data.balanced ? t('cf.ok') : t('cf.off')}
      </p>
    </section>
  );
}

type Side = 'customers' | 'suppliers';
interface PartnerRow {
  partner: string | null;
  opening: number;
  debit: number;
  credit: number;
  closing: number;
}
interface PartnerLedgerData {
  side: Side;
  from: string;
  to: string;
  rows: PartnerRow[];
  totals: Omit<PartnerRow, 'partner'>;
}
interface PartnerLines {
  opening: number;
  closing: number;
  more: boolean;
  lines: { id: string; entry: string; number: number; date: string; reference: string | null; memo: string; label: string | null; debit: number; credit: number; balance: number }[];
}

const side = (n: number) => (n ? money(n) : '');
const amount = (n: number) => (n === 0 ? '—' : money(n));

export function PartnerLedger({ from, to, version, onEntry }: { from: string; to: string; version: number; onEntry: (id: string) => void }) {
  const [which, setWhich] = useState<Side>('customers');
  const { data } = useAction<PartnerLedgerData>('accounting.partner_ledger', { side: which, from, to, v: version });
  const [open, setOpen] = useState<PartnerRow | null>(null);
  const current = data && data.side === which && data.from === from && data.to === to ? data : null;
  const name = (p: string | null) => p ?? t('pl2.no_partner');
  const csv = () =>
    current &&
    downloadCsv(
      `partner-ledger-${which}-${from}-${to}.csv`,
      [t('pl2.partner'), t('acc.opening'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')],
      current.rows.map((r) => [name(r.partner), plain(r.opening), plain(r.debit), plain(r.credit), plain(r.closing)]),
    );
  return (
    <section className="panel rep-wide statement" aria-labelledby="pl2-title">
      <div className="panel-head">
        <div>
          <h2 id="pl2-title">{t('pl2.title')}</h2>
          <p className="muted">{t(which === 'customers' ? 'pl2.hint_customers' : 'pl2.hint_suppliers')}</p>
        </div>
        <div className="row-actions">
          <div className="segmented" role="radiogroup" aria-label={t('pl2.title')}>
            {(['customers', 'suppliers'] as const).map((s) => (
              <button key={s} role="radio" aria-checked={which === s} className={which === s ? 'active' : ''} onClick={() => setWhich(s)}>
                {t(`pl2.${s}`)}
              </button>
            ))}
          </div>
          <Button variant="ghost" icon="download" disabled={!current} onClick={csv}>
            {t('rep.download')}
          </Button>
        </div>
      </div>
      {!current ? (
        <Skeleton h={240} />
      ) : (
        <Table
          caption={t('pl2.title')}
          head={[t('pl2.partner'), t('acc.opening'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')]}
          phoneHide={[1, 2, 3]}
          empty={t('pl2.empty')}
          rows={current.rows.map((r) => ({
            key: r.partner ?? '',
            onClick: () => setOpen(r),
            cells: [
              <span className={r.partner ? '' : 'muted'}>{name(r.partner)}</span>,
              <span className="tabular">{amount(r.opening)}</span>,
              <span className="tabular">{side(r.debit)}</span>,
              <span className="tabular">{side(r.credit)}</span>,
              <span className={`tabular strong${r.closing < 0 ? ' negative' : ''}`}>{amount(r.closing)}</span>,
            ],
          }))}
          foot={[
            t('acc.total'),
            <span className="tabular strong">{money(current.totals.opening)}</span>,
            <span className="tabular strong">{money(current.totals.debit)}</span>,
            <span className="tabular strong">{money(current.totals.credit)}</span>,
            <span className="tabular strong">{money(current.totals.closing)}</span>,
          ]}
        />
      )}
      {open ? <PartnerDialog side={which} row={open} name={name(open.partner)} from={from} to={to} onClose={() => setOpen(null)} onEntry={onEntry} /> : null}
    </section>
  );
}

function PartnerDialog({
  side: s,
  row,
  name,
  from,
  to,
  onClose,
  onEntry,
}: {
  side: Side;
  row: PartnerRow;
  name: string;
  from: string;
  to: string;
  onClose: () => void;
  onEntry: (id: string) => void;
}) {
  const { data } = useAction<PartnerLines>('accounting.partner_lines', { side: s, partner: row.partner, from, to });
  const download = () =>
    data &&
    downloadCsv(
      `partner-${name.replace(/[^\p{L}\p{N}]+/gu, '-')}-${from}-${to}.csv`,
      [t('acc.c_date'), t('acc.c_no'), t('acc.c_reference'), t('acc.c_memo'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')],
      [['', '', '', t('acc.opening'), '', '', plain(data.opening)], ...data.lines.map((l) => [l.date, l.number, l.reference ?? '', l.memo, plain(l.debit), plain(l.credit), plain(l.balance)])],
    );
  return (
    <Dialog
      title={name}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="secondary" icon="download" disabled={!data} onClick={download}>
          {t('rep.download')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <div className="acc-ledger">
          <dl className="fiscal-stats">
            <div className="fiscal-stat">
              <dt>{t('acc.opening_at', { date: day(from) })}</dt>
              <dd>{money(data.opening)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('acc.c_debit')}</dt>
              <dd>{money(row.debit)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('acc.c_credit')}</dt>
              <dd>{money(row.credit)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('acc.closing_at', { date: day(to) })}</dt>
              <dd>{money(data.closing)}</dd>
            </div>
          </dl>
          <Table
            head={[t('acc.c_date'), t('acc.c_memo'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')]}
            sortable={false}
            phoneHide={[]}
            left={[1]}
            empty={t('acc.no_lines')}
            rows={data.lines.map((l) => ({
              key: l.id,
              onClick: () => onEntry(l.entry),
              cells: [
                day(l.date),
                <span className="acc-memo">
                  {l.memo}
                  <span className="muted small">{[t('acc.entry_title', { n: l.number }), l.reference].filter(Boolean).join(' · ')}</span>
                </span>,
                <span className="tabular">{side(l.debit)}</span>,
                <span className="tabular">{side(l.credit)}</span>,
                <span className={`tabular strong${l.balance < 0 ? ' negative' : ''}`}>{money(l.balance)}</span>,
              ],
            }))}
          />
          {data.more ? <p className="muted acc-more">{t('acc.more_lines')}</p> : null}
        </div>
      )}
    </Dialog>
  );
}
